import Sentry from './sentry';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { KindeProvider } from '@kinde-oss/kinde-auth-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import './App.css';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
    mutations: {
      retry: (failureCount, error) => {
        // Retry once on 5xx errors
        if (failureCount >= 1) return false;
        const status = (error as { status?: number }).status;
        return typeof status === 'number' && status >= 500;
      },
    },
  },
});

// Capture unhandled query/mutation errors in Sentry
queryClient.getQueryCache().config.onError = (error) => {
  Sentry.captureException(error, { tags: { source: 'react-query', type: 'query' } });
};
queryClient.getMutationCache().config.onError = (error) => {
  Sentry.captureException(error, { tags: { source: 'react-query', type: 'mutation' } });
};

const requiredEnvVars = [
  'VITE_KINDE_CLIENT_ID',
  'VITE_KINDE_DOMAIN',
  'VITE_KINDE_REDIRECT_URI',
  'VITE_KINDE_LOGOUT_URI',
  'VITE_KINDE_AUDIENCE',
  'VITE_API_URL',
] as const;

for (const key of requiredEnvVars) {
  if (!import.meta.env[key]) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
}

createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <KindeProvider
      clientId={import.meta.env.VITE_KINDE_CLIENT_ID}
      domain={import.meta.env.VITE_KINDE_DOMAIN}
      redirectUri={import.meta.env.VITE_KINDE_REDIRECT_URI}
      logoutUri={import.meta.env.VITE_KINDE_LOGOUT_URI}
      audience={import.meta.env.VITE_KINDE_AUDIENCE}
    >
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </QueryClientProvider>
    </KindeProvider>
  </StrictMode>,
);
